import { LegalChrome } from './LegalChrome';
import { siteOrigin } from '../config';
import { MAILING_ADDRESS, SUPPORT_EMAIL } from './constants';

const LAST_UPDATED = 'June 22, 2026';

export function PrivacyPolicyPage() {
  const deleteAccountUrl = `${siteOrigin()}/legal/delete-account`;

  return (
    <LegalChrome>
      <h1 className="legal-h1">Privacy Policy</h1>
      <p className="legal-muted">Last updated: {LAST_UPDATED}</p>

      <p className="legal-p">
        Elyxora Works, LLC (“Elyxora,” “we,” “us,” or “our”) operates the Kynward app, website, and
        related services (collectively, the “Services”).
      </p>
      <p className="legal-p">
        This Privacy Policy explains what information we collect, how we use it, when we share it,
        and the choices you have.
      </p>

      <h2 className="legal-h2">1. Information We Collect</h2>
      <p className="legal-p">
        We collect information you provide directly, information collected automatically when you
        use the Services, and information from third parties you choose to connect.
      </p>

      <p className="legal-sub">Information you provide</p>
      <p className="legal-p">
        When you create an account or use the Services, we may collect information such as:
      </p>
      <ul className="legal-ul">
        <li className="legal-li">
          your name, display name, username, email address, phone number, or login details
        </li>
        <li className="legal-li">your age, date of birth, or age confirmation</li>
        <li className="legal-li">
          your profile photo, bio, interests, onboarding responses, and profile preferences
        </li>
        <li className="legal-li">your city or general location you choose to provide</li>
        <li className="legal-li">
          messages, replies, support requests, reports, and other content you submit through the
          Services
        </li>
        <li className="legal-li">
          subscription and purchase information, such as plan status, renewal status, and
          transaction-related details
        </li>
      </ul>

      <p className="legal-sub">Optional sensitive profile information</p>
      <p className="legal-p">
        Some profile information you choose to provide may be considered sensitive in certain
        jurisdictions, such as gender-related information, LGBTQ+ preferences, or inclusion
        preferences. We collect this information only when you choose to provide it, and we use it
        to operate features you request, such as matching and space preferences.
      </p>

      <p className="legal-sub">Information collected automatically</p>
      <p className="legal-p">
        When you use the Services, we may automatically collect certain technical and usage
        information, including:
      </p>
      <ul className="legal-ul">
        <li className="legal-li">device type, operating system, browser type, and app version</li>
        <li className="legal-li">IP address and general location inferred from IP address</li>
        <li className="legal-li">device identifiers and push notification tokens</li>
        <li className="legal-li">log data, diagnostics, crash information, and performance data</li>
        <li className="legal-li">
          usage information, such as screens viewed, features used, and actions taken
        </li>
      </ul>

      <p className="legal-sub">Information from third parties</p>
      <p className="legal-p">
        If you use third-party services with Kynward, we may receive information from them. For
        example:
      </p>
      <ul className="legal-ul">
        <li className="legal-li">from Google or Apple when you sign in using those services</li>
        <li className="legal-li">
          from Stripe, Apple, or Google Play for subscription and purchase status
        </li>
        <li className="legal-li">
          from Spotify if you choose to use music-related features connected to Spotify
        </li>
      </ul>

      <h2 className="legal-h2">2. Photos, Camera, and Media</h2>
      <p className="legal-p">
        With your permission, we may access your device camera, photo library, or media storage so
        you can upload photos and use related features in the Services.
      </p>
      <p className="legal-p">
        We use these permissions only for features you choose to use. You can manage these
        permissions in your device settings, though some features may not work without them.
      </p>

      <h2 className="legal-h2">3. Location Information</h2>
      <p className="legal-p">
        We may collect or infer location information to provide location-based features, such as
        helping place you in city-based experiences or matching features.
      </p>
      <p className="legal-p">This may include:</p>
      <ul className="legal-ul">
        <li className="legal-li">city or area you provide</li>
        <li className="legal-li">approximate location derived from your IP address</li>
        <li className="legal-li">precise device location, if you grant permission</li>
      </ul>
      <p className="legal-p">You can control location permissions in your device settings.</p>

      <h2 className="legal-h2">4. How We Use Information</h2>
      <p className="legal-p">We use information we collect to:</p>
      <ul className="legal-ul">
        <li className="legal-li">create and manage user accounts</li>
        <li className="legal-li">provide, operate, and improve the Services</li>
        <li className="legal-li">personalize your experience</li>
        <li className="legal-li">match you with Circles, users, and related features</li>
        <li className="legal-li">
          display your profile and content to other users as part of the Services
        </li>
        <li className="legal-li">send service messages, emails, and notifications</li>
        <li className="legal-li">process subscriptions, purchases, and renewals</li>
        <li className="legal-li">respond to support requests</li>
        <li className="legal-li">
          monitor performance, troubleshoot issues, and improve reliability
        </li>
        <li className="legal-li">
          detect, investigate, and prevent fraud, spam, abuse, harassment, and rule violations
        </li>
        <li className="legal-li">enforce our Terms, Community Guidelines, and other policies</li>
        <li className="legal-li">comply with legal obligations</li>
      </ul>

      <h2 className="legal-h2">5. How Information Is Shared</h2>
      <p className="legal-p">We do not sell your personal information.</p>
      <p className="legal-p">We may share information in the following ways:</p>

      <p className="legal-sub">With other users</p>
      <p className="legal-p">
        Because Kynward is a social product, certain information is visible to other users as part
        of the Services. This may include your display name, profile photo, bio, interests, city or
        general area, and content you choose to share in Circles or other in-app conversations.
      </p>

      <p className="legal-sub">With service providers</p>
      <p className="legal-p">
        We use third-party providers to help operate the Services, including providers for hosting,
        databases, email delivery, payments, authentication, moderation, notifications, and optional
        connected features. These providers may process information on our behalf as needed to
        provide their services.
      </p>

      <p className="legal-sub">For payments and subscriptions</p>
      <p className="legal-p">
        If you make purchases or subscribe, relevant information may be processed by Stripe, Apple,
        or Google, depending on where the purchase happens.
      </p>

      <p className="legal-sub">For safety, legal, and security reasons</p>
      <p className="legal-p">
        We may disclose information if reasonably necessary to comply with the law, enforce our
        policies, investigate abuse or fraud, or protect the rights, safety, and security of
        Elyxora, our users, or others.
      </p>

      <p className="legal-sub">Business transfers</p>
      <p className="legal-p">
        If we are involved in a merger, acquisition, reorganization, sale of assets, or similar
        transaction, your information may be transferred as part of that process.
      </p>

      <h2 className="legal-h2">6. Third-Party Services We Use</h2>
      <p className="legal-p">
        Depending on the features you use, we may use third-party services such as:
      </p>
      <ul className="legal-ul">
        <li className="legal-li">MySQL for core account and app data</li>
        <li className="legal-li">MongoDB for chat messages and related content</li>
        <li className="legal-li">
          Redis for caching, messaging, rate limiting, and abuse-prevention functions
        </li>
        <li className="legal-li">SendGrid for transactional emails</li>
        <li className="legal-li">Stripe for web payments and subscriptions</li>
        <li className="legal-li">
          Apple for Sign in with Apple, in-app purchases, and subscription verification
        </li>
        <li className="legal-li">
          Google for Google Sign-In and Google Play subscription verification
        </li>
        <li className="legal-li">OpenAI for moderation and trust-and-safety related processing</li>
        <li className="legal-li">PostHog for product analytics and usage measurement</li>
        <li className="legal-li">Spotify for optional music-related features</li>
        <li className="legal-li">Expo and browser or device push services for notifications</li>
      </ul>
      <p className="legal-p">
        These third parties may have their own privacy policies and practices.
      </p>
      <p className="legal-p">
        Some trust-and-safety processing may use automated systems, including artificial
        intelligence tools, to help detect spam, abuse, and policy violations. This may include
        processing of messages, profile content, reports, and related metadata for moderation and
        safety purposes.
      </p>

      <h2 className="legal-h2">7. Safety and Moderation</h2>
      <p className="legal-p">
        To help keep Kynward safe, we may review, monitor, or process content and account activity
        for trust, safety, moderation, spam prevention, and abuse prevention purposes.
      </p>
      <p className="legal-p">
        This may include user-generated content, reports, blocks, complaints, and technical signals
        associated with suspicious or harmful activity. Some of this processing may use automated
        systems or third-party tools.
      </p>

      <h2 className="legal-h2">8. Notifications and Communications</h2>
      <p className="legal-p">
        We may send service-related emails, account alerts, subscription notices, and push
        notifications related to your use of the Services.
      </p>
      <p className="legal-p">
        You can control push notifications through your device or browser settings and, where
        available, through in-app settings.
      </p>

      <h2 className="legal-h2">9. Data Retention</h2>
      <p className="legal-p">
        We retain information for as long as reasonably necessary to provide the Services, maintain
        records, investigate incidents, enforce our agreements, and comply with legal obligations.
      </p>
      <p className="legal-p">
        Retention periods vary depending on the type of information. For example, account
        information may be kept while your account is active, while billing, support, moderation,
        and safety records may be kept longer where necessary.
      </p>

      <h2 className="legal-h2">10. Account Creation and Deletion</h2>
      <p className="legal-p">Users can create an account in Kynward.</p>
      <p className="legal-p">
        If you create an account, you may request deletion of your account and associated data
        through the app and through our web-based deletion request page.
      </p>
      <p className="legal-p">
        You can also delete your account in the Kynward app under Settings → Account → Delete
        account.
      </p>
      <p className="legal-p">
        When you delete your account, we will delete or de-identify your personal information within
        a reasonable period, except where we need to retain certain information for legitimate
        business, safety, fraud prevention, payment, tax, dispute resolution, or legal compliance
        reasons.
      </p>
      <p className="legal-p">Please note that:</p>
      <ul className="legal-ul">
        <li className="legal-li">
          content you previously shared with other users may remain visible to them, including
          copies held in their accounts or on their devices
        </li>
        <li className="legal-li">
          other people’s chat histories may still show past content or metadata where messages were
          not authored only by you, or where information is not stored solely under your user
          identifier
        </li>
        <li className="legal-li">
          certain circle- or group-level records (for example group chat data or scheduled automated
          jobs) may remain; they are not always treated the same as personal account data
        </li>
        <li className="legal-li">
          some technical cache entries (for example keys that combine a circle identifier and your
          user identifier) may not be fully scanned or removed at deletion; they typically expire on
          their own or quickly become harmless
        </li>
        <li className="legal-li">
          backups, server logs, and data held by subprocessors (such as payment providers or email
          services) may retain information under those systems’ retention practices and our
          processor agreements
        </li>
        <li className="legal-li">
          internal operator, beta, or audit tooling (including operator audit logs where used) may
          not be cleared when you delete your account and may not be equivalent to ordinary profile
          data; we may update this description if our practices change or as required by law
        </li>
        <li className="legal-li">
          certain records may be retained where required or permitted by law
        </li>
      </ul>
      <p className="legal-p">
        If you subscribed through the Apple App Store or Google Play, deleting your Kynward account
        does not automatically cancel your store subscription. You must manage subscriptions through
        Apple or Google.
      </p>
      <p className="legal-p">
        Web page for account deletion requests (including if you no longer have the app):
      </p>
      <p className="legal-p">
        <a className="legal-a" href="/legal/delete-account">
          {deleteAccountUrl}
        </a>
      </p>

      <h2 className="legal-h2">11. Your Choices and Rights</h2>
      <p className="legal-p">
        Depending on where you live, you may have rights regarding your personal information,
        including the right to know what we collect, access, correct, delete, or obtain a copy of
        certain information, and the right to opt out of certain processing.
      </p>
      <p className="legal-p">
        <strong>California residents (CCPA/CPRA).</strong> California residents may have the right
        to know the categories and specific pieces of personal information we collect, the purposes
        for which we use it, the categories of sources from which we collect it, and the categories
        of third parties with whom we share it. California residents may also have the right to
        delete personal information, correct inaccurate personal information, and limit the use and
        disclosure of sensitive personal information, subject to certain exceptions. Kynward does
        not sell your personal information and does not share it for cross-context behavioral
        advertising. If you use a Global Privacy Control (GPC) signal where we are required to honor
        it, we will treat it as a valid opt-out request to the extent required by applicable law.
      </p>
      <p className="legal-p">
        <strong>Virginia, Colorado, Texas, and other U.S. state privacy laws.</strong> Depending on
        your state of residence, you may have similar rights to access, correct, delete, obtain a
        copy of, or opt out of certain processing of your personal information, including targeted
        advertising, sale of personal information, or profiling in furtherance of decisions that
        produce legal or similarly significant effects, where applicable. Kynward does not sell
        personal information or share it with data brokers.
      </p>
      <p className="legal-p">
        You may also control certain permissions, such as camera, photo, and location access,
        through your device settings. For more self-service options, see our{' '}
        <a className="legal-a" href="/privacy-choices">
          Privacy Choices
        </a>{' '}
        page.
      </p>
      <p className="legal-p">
        To make a privacy-related request, contact us using the information below. We may need to
        verify your identity before fulfilling a request.
      </p>

      <h2 className="legal-h2">12. Children’s Privacy</h2>
      <p className="legal-p">
        Kynward is intended only for users who are 18 years of age or older. We do not knowingly
        collect personal information from anyone under 18.
      </p>

      <h2 className="legal-h2">13. Security</h2>
      <p className="legal-p">
        We use reasonable administrative, technical, and organizational safeguards designed to
        protect personal information. However, no method of transmission or storage is completely
        secure, and we cannot guarantee absolute security.
      </p>

      <h2 className="legal-h2">14. International Data Transfers</h2>
      <p className="legal-p">
        If you use the Services from outside the United States, your information may be transferred
        to, stored in, and processed in the United States or other countries where we or our service
        providers operate.
      </p>

      <h2 className="legal-h2">15. Changes to This Privacy Policy</h2>
      <p className="legal-p">
        We may update this Privacy Policy from time to time. If we make material changes, we will
        update the “Last updated” date and may provide additional notice where required by law.
      </p>

      <h2 className="legal-h2">16. Contact Us</h2>
      <p className="legal-p">
        If you have questions about this Privacy Policy or want to make a privacy request, contact
        us at:
      </p>
      <p className="legal-p" style={{ fontWeight: 600 }}>
        Elyxora Works, LLC
      </p>
      <div className="legal-contact-line">
        <span>Email: </span>
        <a className="legal-a" href={`mailto:${SUPPORT_EMAIL}`}>
          {SUPPORT_EMAIL}
        </a>
      </div>
      <p className="legal-p">Address: {MAILING_ADDRESS}</p>
    </LegalChrome>
  );
}
